import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { AdminClaimsSection } from "./components/AdminClaimsSection";
import { AdminCreateQuoteButton } from "./components/AdminCreateQuoteButton";
import { AdminHijackUserButton } from "./components/AdminHijackUserButton";
import { AdminPaymentsSection } from "./components/AdminPaymentsSection";
import { AdminPoliciesSection } from "./components/AdminPoliciesSection";
import { AdminQuotesSection } from "./components/AdminQuotesSection";
import { CustomerDetailsSection } from "./components/AdminCustomerDetailsSection";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { KIND, SIZE as BUTTON_SIZE } from "baseui/button";
import { Placeholder } from "kanopi-components/Placeholder";
import { SummaryCard } from "./components/SummaryCard";
import { Tab, Tabs } from "baseui/tabs-motion";
import { useCustomers } from "./hooks/useCustomers";
import { useUser } from "kw/auth/useUser";
import { useGlobal } from "kanopi-components/useGlobal";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { AdminCreateClaimButton } from "./components/AdminCreateClaimButton";

export const CustomerDetailsPage = ({ customerId }: { customerId: string }) => {
  const { customer, loading, fetchCustomer } = useCustomers();
  const user = useUser();
  const [, setHijack] = useGlobal("hijack");
  const [activeKey, setActiveKey] = React.useState("0");
  useEffect(() => {
    if (customerId) {
      fetchCustomer(customerId);
    }
  }, [customerId]);
  useEffect(() => {
    const customerHasPolicies = customer?.policies?.length > 0;
    const customerHasQuotes = customer?.quotes?.length > 0;
    const nextUrl = customerHasPolicies
      ? "/policies"
      : customerHasQuotes
      ? "/quote/ready"
      : "/quote/ready"; // TODO: If no quotes, should probably redirect to quote flow with a product to select
    setHijack({
      nextUrl: nextUrl,
      releaseToUrl: `/admin/customers/${customerId}`,
    });
  }, [customer]);

  return (
    <>
      <Helmet>
        <title>{`${customer?.details?.firstName} ${customer?.details?.lastName} - ${user?.organization?.name}`}</title>
      </Helmet>
      <Placeholder loading={loading}>
        <VerticalSpacer>
          <HeadingActionsBar
            heading={
              (customer?.details?.firstName &&
                `${customer?.details?.firstName} ${customer?.details?.lastName}`) ||
              ""
            }
          />
          <SummaryCard
            data-testid="adminSummarySection"
            title="Customer details"
            ctas={[
              <AdminHijackUserButton
                key={0}
                userId={customer?.details.id}
                userIsActive={customer?.details.isActive}
                kind={KIND.secondary}
              />,
            ]}
          >
            <CustomerDetailsSection customer={customer?.details} />
          </SummaryCard>
          <SummaryCard showHeader={false}>
            <Tabs
              activeKey={activeKey}
              onChange={({ activeKey }) => {
                setActiveKey(activeKey);
              }}
              activateOnFocus
            >
              <Tab title="Quotes" data-testid="customerQuotesSection">
                <VerticalSpacer gap="inner-section">
                  <HorizontalSpacer justifyContent="right">
                    <AdminCreateQuoteButton
                      customer={customer?.details}
                      size={BUTTON_SIZE.compact}
                    />
                  </HorizontalSpacer>
                  <AdminQuotesSection quotes={customer?.quotes} />
                </VerticalSpacer>
              </Tab>
              <Tab title="Policies" data-testid="customerPoliciesSection">
                <AdminPoliciesSection policies={customer?.policies} />
              </Tab>
              <Tab title="Claims" data-testid="customerClaimsSection">
                <HorizontalSpacer justifyContent="right">
                  <AdminCreateClaimButton
                    customer={customer?.details}
                    size={BUTTON_SIZE.compact}
                  />
                </HorizontalSpacer>
                <AdminClaimsSection claims={customer?.claims} />
              </Tab>
            </Tabs>
          </SummaryCard>
          <SummaryCard data-testid="adminPaymentsSection" title="Payments">
            <AdminPaymentsSection payments={customer?.payments} />
          </SummaryCard>
        </VerticalSpacer>
      </Placeholder>
    </>
  );
};
